/**
 * FashPlayPage
 * Unified video generation experience
 * 
 * Flow:
 * 1. Check User (IndexedDB) -> if exists, maybe skip to Waiting?
 * 2. Upload Photos (Selfie [Required], Body [Optional])
 * 3. User Stats (Age, Height, Weight)
 * 4. Body Type Selection (Only if body photo was skipped)
 * 5. Avatar Generation
 * 6. Waiting Screen (Video Generation)
 * 7. Result
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { BodyTypeSelector, BodyTypeInfo } from '../../components/BodyTypeSelector/BodyTypeSelector';
import { AvatarPreview } from '../../components/AvatarPreview/AvatarPreview';

import { PhotoUpload } from '../../components/shared/PhotoUpload';
import { UserStatsStep } from '../../components/shared/UserStatsStep';
import { FashionOrbitLoader } from '../../components/Loader/FashionOrbitLoader';
import { AvatarRevealLoader } from '../../components/Loader/AvatarRevealLoader';
import { WaitingOrchestrator } from '../../components/WaitingScreen';
import { WaitingTab } from '../../components/WaitingScreen/TopNavBar/TopNavBar';
import { ErrorStep } from '../../components/shared/ErrorStep';
import { FashPlayResult } from '../../components/shared/FashPlayResult';
import { indexedDBService, StoredUser } from '../../services/IndexedDBService';
import { environmentConfig } from '../../config/environment';
import './FashPlayPage.css';

type PlayStep =
    | 'loading'
    | 'upload-photos'
    | 'user-stats'
    | 'body-type-selection'
    | 'generating-avatar'
    | 'avatar-preview'
    | 'generating-video'
    | 'result'
    | 'error';

export const FashPlayPage: React.FC = () => {
    const navigate = useNavigate();
    const { state: appState, dispatch } = useAppContext();
    const productId = appState.fashPlayProduct?.productId || (appState.demoMode ? 'DEMO-PLAY-001' : '');

    // Helper Maps for Demo Mode
    const genderMap: Record<string, 'male'|'female'> = React.useMemo(() => ({
        melis: 'female',
        can: 'male',
        emre: 'male'
    }), []);
    const bodyTypeMap: Record<string, string> = React.useMemo(() => ({
        melis: 'pear',
        can: 'trapezoid',
        emre: 'rectangle'
    }), []);
    const statsMap = React.useMemo(() => ({
        melis: { age: 30, height: 165, weight: 58 },
        can: { age: 28, height: 180, weight: 76 },
        emre: { age: 33, height: 185, weight: 82 }
    }), []);

    const isDemoActive = !!appState.demoMode;
    const demoPersonKey = appState.demoPersonId || 'melis';

    // Step State
    const [step, setStep] = useState<PlayStep>(isDemoActive ? 'upload-photos' : 'loading');
    const [isCheckingUser, setIsCheckingUser] = useState(!isDemoActive);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // User Data State
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [storedUser, setStoredUser] = useState<StoredUser | null>(null);
    const [gender, setGender] = useState<'female' | 'male' | null>(
        isDemoActive ? (genderMap[demoPersonKey] || 'female') : null
    );
    const [selectedBodyType, setSelectedBodyType] = useState<string | null>(
        isDemoActive ? (bodyTypeMap[demoPersonKey] || 'hourglass') : null
    );
    const [selectedBodyTypeInfo, setSelectedBodyTypeInfo] = useState<BodyTypeInfo | null>(
        isDemoActive ? {
            id: bodyTypeMap[demoPersonKey] || 'hourglass',
            title: 'Otomatik Tespit',
            description: 'Analiz ile belirlenen vücut ölçüleri',
            imagePath: ''
        } : null
    );
    const [selfieImage, setSelfieImage] = useState<string | null>(
        isDemoActive ? `/assets/showcase/selfie_${demoPersonKey}.jpg` : null
    );
    const [bodyImage, setBodyImage] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [hasBodyPhoto, setHasBodyPhoto] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const [detectedRegions, setDetectedRegions] = useState<{ upper: string; waist: string; lower: string } | null>(null);
    
    // Stats State
    const [userStats, setUserStats] = useState<{ age: number; height: number; weight: number } | null>(
        isDemoActive ? (statsMap[demoPersonKey as keyof typeof statsMap] || { age: 25, height: 170, weight: 65 }) : null
    );

    // Result State
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [activeResultTab, setActiveResultTab] = useState<WaitingTab>('product');
    const [showDislikeModal, setShowDislikeModal] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [attemptNumber, setAttemptNumber] = useState(1);
    const [isApprovingAvatar, setIsApprovingAvatar] = useState(false);
    
    // Waiting Screen State
    const isVideoReadyRef = useRef<boolean>(false);
    const currentProductSkuRef = useRef<string | null>(null);

    // API URL helper
    const getApiUrl = useCallback(() => {
        let apiUrl = environmentConfig.api.combinationsApi || 'https://fashion-connect-production.up.railway.app';
        if (apiUrl.endsWith('/api/v1')) {
            apiUrl = apiUrl.replace('/api/v1', '');
        }
        return apiUrl;
    }, []);

    const useLegacyAiEndpoints =
        process.env.REACT_APP_USE_LEGACY_AI_ENDPOINTS === 'true' ||
        (typeof window !== 'undefined' && window.sessionStorage.getItem('FC_USE_LEGACY_AI_ENDPOINTS') === 'true');

    // Generate Video (with 10 minute timeout for long-running video generation)
    const generateVideo = useCallback(async (mode: 'fit' | 'persona', avatar?: string | null, overrideGender?: string, overrideBodyType?: string, explicitUserId?: string, forceRegenerate?: boolean) => {
        if (!productId) {
            setError('Ürün seçilmedi');
            setStep('error');
            return;
        }

        const activeGender = overrideGender || gender;
        const activeBodyType = overrideBodyType || selectedBodyType;

        if (!activeGender) {
            console.error('❌ [FashPlay] Missing gender in generateVideo');
            setError('Cinsiyet verisi eksik');
            setStep('error');
            return;
        }

        // Reset video ready state
        isVideoReadyRef.current = false;
        currentProductSkuRef.current = productId;
        
        setStep('generating-video');
        setError(null);

        if (appState.demoMode) {
            console.log('🎬 [FashPlay] Running in demo mode, returning pre-cached video');
            await new Promise(resolve => setTimeout(resolve, 3000));
            const personKey = appState.demoPersonId || 'melis';
            const cacheUrl = `/assets/showcase/video_${personKey}.mp4`;
            setVideoUrl(cacheUrl);
            isVideoReadyRef.current = true;
            return;
        }

        // No need for long timeout - job creation is instant
        const createTimeout = setTimeout(() => {
            throw new Error('Job creation timeout');
        }, 30000); // 30 seconds

        try {
            // Validate brandId
            if (!appState.brandId) {
                throw new Error('Brand ID is missing. Please initialize the SDK properly.');
            }

            const payload = {
                productId,
                userId: explicitUserId || storedUser?.userId, 
                avatarUrl: avatar || undefined, 
                gender: activeGender,
                bodyType: activeBodyType,
                mode: mode, 
                withSelfie: mode === 'persona',
                forceRegenerate: forceRegenerate || false,
                brandId: appState.brandId,
            };

            console.log('🎬 [FashPlay] Creating video generation job:', payload);

            // Step 1: Create job
            const createPath = useLegacyAiEndpoints ? '/api/v1/fash-play/generate' : '/api/v1/media/video/jobs';
            const createResponse = await fetch(`${getApiUrl()}${createPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            clearTimeout(createTimeout);

            if (!createResponse.ok) {
                const errorData = await createResponse.json();
                throw new Error(errorData.error || 'Job creation failed');
            }

            const { jobId } = await createResponse.json();
            console.log(`✅ [FashPlay] Job created: ${jobId}`);

            // Step 2: Poll for completion
            const pollInterval = 3000; // 3 seconds
            const maxPolls = 200; // 200 * 3s = 10 minutes

            for (let i = 0; i < maxPolls; i++) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));

                const statusPath = useLegacyAiEndpoints
                    ? `/api/v1/fash-play/generate-status/${jobId}`
                    : `/api/v1/media/video/jobs/${jobId}`;
                const statusResponse = await fetch(`${getApiUrl()}${statusPath}`);

                if (!statusResponse.ok) {
                    throw new Error('Failed to check job status');
                }

                const statusData = await statusResponse.json();

                if (statusData.status === 'completed') {
                    console.log('✅ [FashPlay] Video generated:', statusData.videoUrl);
                    setVideoUrl(statusData.videoUrl);
                    isVideoReadyRef.current = true;
                    return;
                }

                if (statusData.status === 'failed') {
                    throw new Error(statusData.error || 'Video generation failed');
                }

                // Still processing or pending, continue polling
                console.log(`⏳ [FashPlay] Job status: ${statusData.status} (poll ${i + 1}/${maxPolls})`);
            }

            // Timeout after max polls
            throw new Error('Video generation timeout - please try again');

        } catch (err: any) {
            clearTimeout(createTimeout);
            console.error('❌ [FashPlay] Video generation error:', err);
            setError(err.message || 'Video oluşturulurken hata oluştu');
            setStep('error');
        }
    }, [productId, getApiUrl, gender, selectedBodyType, storedUser, appState.brandId, useLegacyAiEndpoints, appState.demoMode, appState.demoPersonId]);

    // Step 5: Generate Avatar (with async polling)
    const generateAvatar = useCallback(async (selfie: string, gender: 'male'|'female', bodyType: string, bodyTypeImagePath?: string) => {
        setStep('generating-avatar');
        setError(null);

        if (appState.demoMode) {
            console.log('🎨 [FashPlay] Running in demo mode, returning pre-cached avatar');
            await new Promise(resolve => setTimeout(resolve, 2000));
            const personKey = appState.demoPersonId || 'melis';
            const cacheUrl = `/assets/showcase/avatar_${personKey}.jpg`;
            setAvatarUrl(cacheUrl);
            setStep('avatar-preview');
            return;
        }

        const createController = new AbortController();
        const createTimeout = setTimeout(() => createController.abort(), 30000);

        try {
            console.log('🎨 [FashPlay] Creating avatar generation job...');

            if (!appState.brandId) {
                throw new Error('Brand ID bulunamadı. Lütfen sayfayı yenileyin.');
            }

            const avatarPayload: any = {
                selfie,
                gender,
                bodyType,
                age: userStats?.age,
                height: userStats?.height,
                weight: userStats?.weight,
                brandId: appState.brandId,
            };
            if (bodyTypeImagePath) {
                avatarPayload.bodyTypeImageFile = bodyTypeImagePath;
            }

            const avatarCreatePath = useLegacyAiEndpoints ? '/api/v1/fash-play/generate-avatar' : '/api/v1/media/image/avatar/jobs';
            const response = await fetch(`${getApiUrl()}${avatarCreatePath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(avatarPayload),
                signal: createController.signal
            });

            clearTimeout(createTimeout);

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Avatar job oluşturulamadı');
            }

            const { jobId } = await response.json();
            console.log(`✅ [FashPlay] Avatar job created: ${jobId}`);

            // Step 2: Poll for avatar generation completion (5 minutes max, 3 second intervals)
            const maxPolls = 100; // 5 minutes (100 * 3 seconds)
            let pollCount = 0;
            const pollInterval = 3000; // 3 seconds

            while (pollCount < maxPolls) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                pollCount++;

                console.log(`🔄 [FashPlay] Polling avatar job ${jobId} (${pollCount}/${maxPolls})...`);

                const avatarStatusPath = useLegacyAiEndpoints
                    ? `/api/v1/fash-play/generate-avatar-status/${jobId}`
                    : `/api/v1/media/image/avatar/jobs/${jobId}`;
                const statusResponse = await fetch(`${getApiUrl()}${avatarStatusPath}`);
                const statusData = await statusResponse.json();

                if (statusData.status === 'completed') {
                    console.log('✅ [FashPlay] Avatar generated:', statusData.avatarUrl);
                    setAvatarUrl(statusData.avatarUrl);
                    setStep('avatar-preview');
                    return;
                } else if (statusData.status === 'failed') {
                    throw new Error(statusData.error || 'Avatar oluşturulamadı');
                }
                // If status is 'pending' or 'processing', continue polling
            }

            // Timeout after max polls
            throw new Error('Avatar oluşturma zaman aşımına uğradı. Lütfen tekrar deneyin.');

        } catch (err: any) {
            clearTimeout(createTimeout);
            console.error('❌ [FashPlay] Avatar generation error:', err);
            
            if (err.name === 'AbortError') {
                setError('Avatar job oluşturma zaman aşımına uğradı. Lütfen tekrar deneyin.');
            } else {
                setError(err.message || 'Avatar oluşturulurken hata oluştu');
            }
            setStep('error');
        }
    }, [getApiUrl, userStats, appState.brandId, useLegacyAiEndpoints, appState.demoMode, appState.demoPersonId]);

    // Step 3: Analysis
    const analyzePhotos = useCallback(async (
        selfie: string, 
        body: string | null, 
        stats: { age: number; height: number; weight: number }
    ) => {
        setError(null);

        try {
            console.log('🧠 [FashPlay] Starting Analysis...');
            
            // If body photo exists, use validate-fast-mode (returns gender + bodyType)
            // If NOT, use validate-selfie (returns gender)
            
            let detectedGender: 'male' | 'female' = 'female';
            let regions: { upper: string; waist: string; lower: string } | null = null;

            if (body) {
                const validatePath = useLegacyAiEndpoints ? '/api/v1/fash-play/validate-fast-mode' : '/api/v1/media/image/validate-fast-mode';
                const response = await fetch(`${getApiUrl()}${validatePath}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        selfieImage: selfie,
                        fullBodyImage: body,
                        metadata: stats
                    })
                });
                
                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Analiz başarısız');
                if (!result.isSelfieValid) throw new Error(result.selfieError || 'Selfie geçersiz');
                if (!result.isBodyValid) throw new Error(result.bodyError || 'Boy fotoğrafı geçersiz');

                detectedGender = result.detectedGender === 'male' ? 'male' : 'female';

                if (result.detectedRegions) {
                    regions = result.detectedRegions;
                }

            } else {
                const validatePath = useLegacyAiEndpoints ? '/api/v1/fash-play/validate-selfie' : '/api/v1/media/image/validate-selfie';
                const response = await fetch(`${getApiUrl()}${validatePath}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ selfieImage: selfie })
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Analiz başarısız');
                if (!result.valid) throw new Error(result.error || 'Selfie geçersiz');

                detectedGender = result.detectedGender || 'female';
            }

            setGender(detectedGender);

            if (regions) {
                setDetectedRegions(regions);
                
                // Boy fotoğrafı üzerinden region'lar başarıyla tespit edildiyse,
                // "Vücudunuzu Özelleştirin" adımını atla ve doğrudan body type ID oluşturarak
                // avatar üretimine geç.
                const autoBodyTypeId = `${detectedGender}-${regions.upper}-${regions.waist}-${regions.lower}`;
                setSelectedBodyType(autoBodyTypeId);
                setSelectedBodyTypeInfo({
                    id: autoBodyTypeId,
                    title: 'Otomatik Tespit',
                    description: 'Analiz ile belirlenen vücut ölçüleri',
                    imagePath: ''
                });
                generateAvatar(selfie, detectedGender, autoBodyTypeId, body || undefined);
            } else {
                setStep('body-type-selection');
            }

        } catch (err: any) {
            console.error('❌ Analysis Error:', err);
            setError(err.message || 'Analiz sırasında bir hata oluştu');
            setStep('error');
        }
    }, [getApiUrl, generateAvatar, useLegacyAiEndpoints]);

    // Sync demo person if changed
    useEffect(() => {
        if (appState.demoMode) {
            const personKey = appState.demoPersonId || 'melis';
            console.log(`👤 [FashPlay] Demo person selected: ${personKey}`);
            const genderMap: Record<string, 'male'|'female'> = {
                melis: 'female',
                can: 'male',
                emre: 'male'
            };
            const bodyTypeMap: Record<string, string> = {
                melis: 'pear',
                can: 'trapezoid',
                emre: 'rectangle'
            };
            const statsMap = {
                melis: { age: 30, height: 165, weight: 58 },
                can: { age: 28, height: 180, weight: 76 },
                emre: { age: 33, height: 185, weight: 82 }
            };

            setSelfieImage(`/assets/showcase/selfie_${personKey}.jpg`);
            setGender(genderMap[personKey] || 'female');
            setSelectedBodyType(bodyTypeMap[personKey] || 'hourglass');
            setUserStats(statsMap[personKey as keyof typeof statsMap] || { age: 25, height: 170, weight: 65 });
            setStep('upload-photos');
            setIsCheckingUser(false);
        }
    }, [appState.demoMode, appState.demoPersonId]);

    // ============================================
    // INITIALIZATION: Check for existing user
    // ============================================
    useEffect(() => {
        const checkExistingUser = async () => {
            if (appState.demoMode) {
                console.log('🔍 [FashPlay] Running in demo mode, bypassing IndexedDB check');
                setStep('upload-photos');
                setIsCheckingUser(false);
                return;
            }
            console.log('🔍 [FashPlay] Checking for existing user...');
            
            try {
                // Check if user has an active session or previous result for THIS product?
                // For now, let's check general user existence.
                const user = await indexedDBService.getUser();

                if (user?.userId && user?.avatarUrl) {
                    console.log('✅ [FashPlay] Found existing user:', user.userId);
                    setStoredUser(user);
                    setAvatarUrl(user.avatarUrl);
                    setGender(user.gender || 'female');
                    setSelectedBodyType(user.bodyType || 'hourglass');
                    
                    // If user exists, we can skip to generation directly?
                    // Or maybe ask if they want to update their info?
                    // Current flow logic: "Check IndexedDB -> generating-video" for returning users usually.
                    // But if they clicked "Start Over" (handleResetUser), we clear DB.
                    // If this is a fresh load and user exists, we likely want to use them.
                    
                    generateVideo('persona', user.avatarUrl, user.gender, user.bodyType, user.userId);
                } else {
                    console.log('📝 [FashPlay] No existing user, starting fresh');
                    setStep('upload-photos');
                }
            } catch (error) {
                console.error('❌ [FashPlay] Error checking user:', error);
                setStep('upload-photos');
            } finally {
                setIsCheckingUser(false);
            }
        };

        if (productId) {
            checkExistingUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]); // generateVideo is used inside but we want to avoid infinite loop or re-runs on function change if not stable. generateVideo is stable now. We can add it to deps but `react-hooks/exhaustive-deps` might complain if we don't. But `checkExistingUser` is defined inside effect. So it uses closure. If we add generateVideo to effect deps, it might re-run if generateVideo changes. generateVideo depends on many things.
    // The previous implementation had `// eslint-disable-next-line react-hooks/exhaustive-deps` which is fine for init logic.

    // ============================================
    // HANDLERS
    // ============================================

    // Handle back navigation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleBack = () => {
        // If in result step, minimize instead of going back
        if (step === 'result') {
            window.parent.postMessage({ type: 'MINIMIZE_WIDGET' }, '*');
            return;
        }

        switch (step) {
            case 'upload-photos':
                navigate(-1);
                break;
            case 'user-stats':
                setStep('upload-photos');
                break;
            case 'body-type-selection':
                setStep('user-stats');
                break;
            case 'avatar-preview':
                setStep('upload-photos');
                break;
            case 'error':
                setStep('upload-photos');
                break;
            default:
                break;
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleMinimize = () => {
        window.parent.postMessage({ type: 'MINIMIZE_WIDGET' }, '*');
    };

    // Step 1 Complete: Photo Upload
    const handlePhotoUploadComplete = useCallback((data: {
        selfieBase64: string;
        bodyBase64: string | null;
        hasBodyPhoto: boolean;
    }) => {
        console.log('📸 [FashPlay] Photo upload complete. Has Body:', data.hasBodyPhoto);
        setSelfieImage(data.selfieBase64);
        setBodyImage(data.bodyBase64);
        setHasBodyPhoto(data.hasBodyPhoto);
        setStep('user-stats');
    }, []);

    // Step 2 Complete: User Stats
    const handleUserStatsComplete = useCallback(async (data: {
        age: number;
        height: number;
        weight: number;
    }) => {
        console.log('📊 [FashPlay] Stats complete:', data);
        setUserStats(data);
        setIsAnalyzing(true); // Start loading
        
        if (!selfieImage) {
            console.error('Selfie image missing in stats complete');
            setStep('error');
            setIsAnalyzing(false);
            return;
        }

        if (appState.demoMode) {
            console.log('📊 [FashPlay] Demo mode detected, bypassing validation API calls.');
            setIsAnalyzing(false);
            const activeGender = gender || 'female';
            const activeBodyType = selectedBodyType || 'hourglass';
            generateAvatar(selfieImage, activeGender, activeBodyType);
            return;
        }
        
        if (bodyImage) {
            // Body photo var, AI analiz etsin
            console.log('🧠 [FashPlay] Body photo exists, starting AI analysis...');
            analyzePhotos(selfieImage, bodyImage, data);
        } else {
            // Body photo yok, direkt body type seçimine geç
            // Önce cinsiyet analizi yap (hızlı, loading göstermeden)
            console.log('👤 [FashPlay] No body photo, detecting gender quickly...');
            try {
                const validatePath = useLegacyAiEndpoints ? '/api/v1/fash-play/validate-selfie' : '/api/v1/media/image/validate-selfie';
                const response = await fetch(`${getApiUrl()}${validatePath}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ selfieImage: selfieImage })
                });
                
                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Cinsiyet analizi başarısız');
                if (!result.valid) throw new Error(result.error || 'Selfie geçersiz');
                
                const detectedGender = result.detectedGender || 'female';
                console.log('✅ [FashPlay] Gender detected:', detectedGender);
                setGender(detectedGender);
                setIsAnalyzing(false); // Stop loading
                setStep('body-type-selection'); // Direkt body type seçimine geç
            } catch (err: any) {
                console.error('❌ [FashPlay] Gender detection error:', err);
                setError(err.message || 'Cinsiyet analizi sırasında hata oluştu');
                setIsAnalyzing(false);
                setStep('error');
            }
        }
    }, [selfieImage, bodyImage, analyzePhotos, getApiUrl, useLegacyAiEndpoints, appState.demoMode, gender, generateAvatar, selectedBodyType]);

    // Step 4: Body Type Selection (Manual)
    const handleBodyTypeSelect = useCallback((bodyTypeId: string, bodyTypeInfo: BodyTypeInfo) => {
        console.log('🧍 [FashPlay] Body type selected:', bodyTypeId);
        setSelectedBodyType(bodyTypeId);
        setSelectedBodyTypeInfo(bodyTypeInfo);
    }, []);

    const handleBodyTypeContinue = useCallback(() => {
        if (selectedBodyType && selfieImage && gender) {
            generateAvatar(selfieImage, gender, selectedBodyType);
        } else {
            console.warn('⚠️ [FashPlay] Cannot continue: Data missing');
        }
    }, [selectedBodyType, selfieImage, gender, generateAvatar]);

    // Avatar Approval - Create user and save to IndexedDB
    const handleAvatarApprove = useCallback(async () => {
        if (!avatarUrl || !gender || !selectedBodyType || !selfieImage) return;

        if (appState.demoMode) {
            console.log('✅ [FashPlay] Avatar approved in demo mode, bypassing backend creation.');
            // Proceed to video generation
            generateVideo('persona', avatarUrl, undefined, undefined, 'demo-user');
            return;
        }

        setIsApprovingAvatar(true);
        try {
            console.log('✅ [FashPlay] Avatar approved, creating user...');

            // Create user in backend
            const response = await fetch(`${getApiUrl()}/api/v1/avatar-users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gender,
                    body_type: selectedBodyType,
                    avatar_url: avatarUrl,
                    front_image_url: selfieImage,
                    created_product_id: productId,
                    age: userStats?.age,
                    height: userStats?.height,
                    weight: userStats?.weight
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Kullanıcı oluşturulamadı');
            }

            const userId = data.user.id;
            console.log('✅ [FashPlay] User created:', userId);

            // Save to IndexedDB
            await indexedDBService.setUser({
                userId,
                avatarUrl,
                gender,
                bodyType: selectedBodyType,
                timestamp: Date.now(),
                age: userStats?.age,
                height: userStats?.height,
                weight: userStats?.weight
            });

            console.log('✅ [FashPlay] User saved to IndexedDB');

            // Proceed to video generation
            generateVideo('persona', avatarUrl, undefined, undefined, userId);

        } catch (err: any) {
            console.error('❌ [FashPlay] User creation error:', err);
            generateVideo('persona', avatarUrl, gender, selectedBodyType);
        } finally {
            setIsApprovingAvatar(false);
        }
    }, [avatarUrl, gender, selectedBodyType, selfieImage, productId, getApiUrl, userStats, generateVideo, appState.demoMode]);

    const handleAvatarRetry = useCallback(() => {
        // Retry means going back to photo upload? Or just re-generating?
        // Usually back to Selfie.
        setAvatarUrl(null);
        setStep('upload-photos');
    }, []);

    // Create New Avatar - Keep user ID but reset avatar creation flow
    const handleCreateNewAvatar = useCallback(() => {
        // Keep the userId (storedUser.userId) to preserve user connection
        // Only reset avatar-related state
        setAvatarUrl(null);
        setSelfieImage(null);
        setSelectedBodyType(null);
        setSelectedBodyTypeInfo(null);
        setDetectedRegions(null);
        setUserStats(null);
        setVideoUrl(null);
        setStep('upload-photos');
        console.log('🔄 [FashPlay] Starting new avatar creation flow while keeping user connection');
    }, []);

    // Check if result is ready for WaitingOrchestrator
    const checkResultReady = useCallback(async (): Promise<boolean> => {
        return isVideoReadyRef.current;
    }, []);

    // Handle WaitingOrchestrator completion
    const handleWaitingComplete = useCallback(() => {
        if (videoUrl) {
            setStep('result');
            setActiveResultTab('product');
        }
    }, [videoUrl]);

    // Handle video like
    const handleVideoLike = useCallback(async () => {
        if (!videoUrl) return;
        
        try {
            if (!appState.brandId) {
                console.error('❌ Brand ID missing for feedback');
                return;
            }

            await fetch(`${getApiUrl()}/api/v1/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    brandId: appState.brandId,
                    userId: storedUser?.userId,
                    feedbackType: 'like',
                    entityType: 'video',
                    entityId: videoUrl,
                    metadata: {
                        productSku: productId,
                        gender,
                        bodyType: selectedBodyType,
                        attemptNumber,
                        waitingType: 'fashplay'
                    }
                })
            });
            console.log('✅ Video liked');
        } catch (err) {
            console.error('❌ Failed to submit like feedback:', err);
        }
    }, [videoUrl, getApiUrl, appState.brandId, storedUser, productId, gender, selectedBodyType, attemptNumber]);

    // Handle video dislike - show modal
    const handleVideoDislike = useCallback(() => {
        setShowDislikeModal(true);
    }, []);

    // Handle dislike reason selection and regenerate video
    const handleDislikeReasonSelect = useCallback(async (reason: string) => {
        if (!videoUrl) return;
        
        if (!appState.brandId) {
            console.error('❌ Brand ID missing for feedback');
            setShowDislikeModal(false);
            return;
        }
        
        setIsRegenerating(true);
        
        try {
            // 1. Submit dislike feedback with current video URL
            await fetch(`${getApiUrl()}/api/v1/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    brandId: appState.brandId,
                    userId: storedUser?.userId,
                    feedbackType: 'dislike',
                    entityType: 'video',
                    entityId: videoUrl,
                    reason,
                    metadata: {
                        productSku: productId,
                        gender,
                        bodyType: selectedBodyType,
                        attemptNumber,
                        waitingType: 'fashplay'
                    }
                })
            });
            console.log('✅ Video dislike feedback submitted');

            // 2. Always invalidate cache to force regeneration
            await fetch(`${getApiUrl()}/api/v1/fash-play/invalidate-cache`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: productId,
                    bodyType: selectedBodyType,
                    gender
                })
            });
            console.log('✅ Video cache invalidated');

            // 3. Increment attempt and reset video state
            const newAttemptNumber = attemptNumber + 1;
            setAttemptNumber(newAttemptNumber);
            setVideoUrl(null);
            isVideoReadyRef.current = false;
            
            // 4. Close modal
            setShowDislikeModal(false);
            setIsRegenerating(false);
            
            // 5. Go to waiting/generating step for regeneration
            setStep('generating-video');
            
            // 6. Regenerate video with forceRegenerate flag
            const mode = avatarUrl ? 'persona' : 'fit';
            generateVideo(mode, avatarUrl, gender || undefined, selectedBodyType || undefined, storedUser?.userId, true);
            
        } catch (err) {
            console.error('❌ Failed to handle dislike:', err);
            setIsRegenerating(false);
            setShowDislikeModal(false);
        }
    }, [videoUrl, getApiUrl, appState.brandId, storedUser, productId, gender, selectedBodyType, attemptNumber, avatarUrl, generateVideo]);

    // ============================================
    // RENDER
    // ============================================

    const renderContent = () => {
        if (isCheckingUser || step === 'loading') {
            return (
                <div className="fashplay-content">
                    <div className="fashplay-loading">
                        <div className="fashplay-loading__spinner" />
                        <p>Yükleniyor...</p>
                    </div>
                </div>
            );
        }

        switch (step) {
            case 'upload-photos':
                return (
                    <div className="fashplay-content">
                        <PhotoUpload 
                            onComplete={handlePhotoUploadComplete}
                            demoMode={false}
                            initialSelfie={selfieImage}
                            onSelectDemoPerson={(personId) => {
                                dispatch({
                                    type: 'HANDLE_MESSAGE',
                                    payload: {
                                        type: 'DEMO_SELECT_PERSON',
                                        payload: { personId }
                                    }
                                });
                            }}
                        />
                    </div>
                );

            case 'user-stats':
                return (
                    <div className="fashplay-content">
                        <UserStatsStep 
                            onComplete={handleUserStatsComplete} 
                            initialData={userStats || undefined}
                            isLoading={isAnalyzing}
                        />
                    </div>
                );



            case 'body-type-selection':
                return (
                    <>
                        <div className="fashplay-content">
                            {gender && (
                                <BodyTypeSelector
                                    gender={gender}
                                    onSelect={handleBodyTypeSelect}
                                    selectedBodyType={selectedBodyType || undefined}
                                    apiBaseUrl={getApiUrl()}
                                    initialValues={detectedRegions as any}
                                />
                            )}
                            <div className="fash-motion-bottom-actions">
                                <button
                                    className="fash-motion-cta-primary"
                                    onClick={handleBodyTypeContinue}
                                    disabled={!selectedBodyType}
                                >
                                    Devam Et
                                </button>
                            </div>
                        </div>
                    </>
                );

            case 'generating-avatar':
                return (
                    <AvatarRevealLoader 
                        selfieUrl={selfieImage || undefined}
                        gender={gender || undefined}
                    />
                );

            case 'avatar-preview':
                return (
                    <div className="fashplay-content">
                        {avatarUrl && gender && selectedBodyType && selectedBodyTypeInfo && selfieImage && (
                            <AvatarPreview
                                avatarData={{
                                    avatarUrl: avatarUrl,
                                    selfieUrl: selfieImage,
                                    gender: gender,
                                    bodyType: selectedBodyType,
                                    bodyTypeTitle: selectedBodyTypeInfo.title,
                                    age: userStats?.age,
                                    height: userStats?.height,
                                    weight: userStats?.weight,
                                }}
                                onApprove={handleAvatarApprove}
                                onReject={handleAvatarRetry}
                                isLoading={isApprovingAvatar}
                            />
                        )}
                    </div>
                );

            case 'generating-video':
                return appState.brandId ? (
                    <WaitingOrchestrator
                        brandId={appState.brandId}
                        waitingType="fashplay"
                        productSku={currentProductSkuRef.current || productId}
                        userId={storedUser?.userId}
                        onComplete={handleWaitingComplete}
                        onResultReady={() => console.log('🎉 [FashPlay] Result ready!')}
                        checkResultReady={checkResultReady}
                        onCreateNewAvatar={handleCreateNewAvatar}
                        userMetadata={{
                            age: userStats?.age,
                            height: userStats?.height,
                            weight: userStats?.weight,
                            bodyType: selectedBodyType || undefined,
                            gender: gender || undefined,
                            avatarUrl: avatarUrl || undefined
                        }}
                    />
                ) : (
                    <FashionOrbitLoader stage="video" />
                );

            case 'result':
                return (
                    <FashPlayResult
                        videoUrl={videoUrl}
                        activeTab={activeResultTab}
                        onTabChange={setActiveResultTab}
                        onLike={handleVideoLike}
                        onDislike={handleVideoDislike}
                        storedUserId={storedUser?.userId}
                        userStats={userStats || undefined}
                        gender={gender}
                        selectedBodyType={selectedBodyType}
                        avatarUrl={avatarUrl}
                        onCreateNewAvatar={handleCreateNewAvatar}
                        showDislikeModal={showDislikeModal}
                        onCloseDislikeModal={() => setShowDislikeModal(false)}
                        onDislikeReasonSelect={handleDislikeReasonSelect}
                        isRegenerating={isRegenerating}
                    />
                );

            case 'error':
                return (
                    <ErrorStep
                        error={error}
                        onRetry={() => setStep('upload-photos')}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className="fashplay-page">
            <div className="fashplay-container">
                {renderContent()}
            </div>
        </div>
    );
};

export default FashPlayPage;
